import React, { Component } from 'react';
import Table, { ColumnProps } from 'antd/lib/table';
import RequirementService, { IRequirementInfo, RequirementPutParam } from '../../service/requirement/RequirementService';
import './page-requirement.less';
import { timestamp2localdate } from '../../utils/dateTools';
import { DatePicker, Modal} from 'antd';
import RequeirementStatesSelect, { getRequirementStates } from '../../components/public/RequeirementStatesSelect';
import RequirementHistoryService, { IRequirementHistoryInfo, IRequirementHistoryInfoList } from '../../service/requirement/RequirementHistoryService';
import moment from 'moment';
import { REQUIREMNET_HISTORY_DELETE_CONFIRM_MESSAGE } from '../../config/strcfg';

interface RequirementHistoryTableProps {
    requirementId: number | null,
}

interface RequirementHistoryRecord extends IRequirementHistoryInfo {
    createTimestamp?: number;
}

interface RequirementHistoryTableState {
    pageData: IRequirementHistoryInfoList | null,
    sourceData: RequirementHistoryRecord[],
    isUpdateHistoryModalVisible: boolean,
    selectedDate: moment.Moment | null;
    selectedStates: number | null;
    selectRequirementId: number | null;
}


//自定义RequirementHistoryTable
class RequirementHistoryTable extends Component<RequirementHistoryTableProps, RequirementHistoryTableState> {
    state: RequirementHistoryTableState = {
        pageData: {
            records: [],
        },
        sourceData: [],
        isUpdateHistoryModalVisible: false,
        selectedDate: null,
        selectedStates: null,
        selectRequirementId: null,

    };

    async componentDidMount() {
        await this.getRequirementHistoryTableData(this.props.requirementId);
    }

    async componentDidUpdate(prevProps: Readonly<RequirementHistoryTableProps>, prevState: Readonly<RequirementHistoryTableState>) {
        if (prevProps.requirementId !== this.props.requirementId) {
            await this.getRequirementHistoryTableData(this.props.requirementId);
        }
    }

    //获取指定页码的查询数据
    getRequirementHistoryTableData = async (requirementId?:number | null) => {
        try {
            const response = await RequirementHistoryService.getRequirementHistoryByRequirementId(requirementId);  
            if (response) {
                const pageData: IRequirementHistoryInfoList = response.data?.requirementHistoryInfoList || [];
                const RequirementRecords = Array.isArray(pageData) ? pageData : [];
                const sortedRecords = RequirementRecords.sort((a: any, b: any) => (a.createTime || 0) - (b.createTime || 0));
                const mappedSourceData = sortedRecords.map((item: any) => ({
                    id: item.id,
                    requirementName: item.requirementName,
                    requirementId: item.requirementId,
                    sales: item.sales,
                    salesName: item.salesName,
                    states: item.states,
                    createTime: timestamp2localdate(item.createTime, false),
                    createTimestamp: item.createTime,
                    
                }));
                this.setState({ sourceData: mappedSourceData });
            }
        } catch (error) {
            console.error('获取需求列表失败:', error);
        }
    };


    //编辑按钮事件
    handleEditBottonClick = (requirementData: RequirementHistoryRecord) => {
        // 展示编辑需求历史记录弹窗并加载初始数据
        this.setState({ isUpdateHistoryModalVisible: true, 
                        selectRequirementId: requirementData.id,
                        selectedStates: requirementData.states, 
                        selectedDate: moment(requirementData.createTime, 'YYYY-MM-DD') }); 
    }

    //删除按钮事件
    handleDeleteBottonClick = (requirementData: RequirementHistoryRecord) => {
        // 展示删除需求历史记录弹窗
        Modal.confirm({
            title: REQUIREMNET_HISTORY_DELETE_CONFIRM_MESSAGE,
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                try {
                    const isLatestRecord = this.isLatestHistoryRecord(requirementData.id);
                    const previousRecord = this.getPreviousHistoryRecord(requirementData.id);
                    await RequirementHistoryService.deleteRequirementHistory(requirementData.id);
                    if (isLatestRecord && previousRecord && typeof previousRecord.states === 'number') {
                        await this.syncRequirementStates(previousRecord.states);
                    }
                    await this.getRequirementHistoryTableData(this.props.requirementId); // 删除后刷新表格
                } catch (error) {
                    console.error('删除失败:', error);
                }
            },
        });
    }

    //更新状态确认按钮事件
    handleUpdateHistoryOk = async () => {
        const { selectedStates, selectedDate, selectRequirementId } = this.state;
        if (typeof selectedStates === 'number' && selectedDate && selectRequirementId !== null) {
            try{
                const isLatestRecord = this.isLatestHistoryRecord(selectRequirementId);
                await RequirementHistoryService.updateRequirementHistory({
                    id: selectRequirementId,
                    states: selectedStates,
                    createTime: selectedDate.startOf('day').toDate().toISOString(),
                });
                if (isLatestRecord) {
                    await this.syncRequirementStates(selectedStates);
                }
                await this.getRequirementHistoryTableData(this.props.requirementId);
            }catch (error) {
                console.error('编辑失败:', error);
            }
            this.setState({ isUpdateHistoryModalVisible: false, selectedStates: null, selectedDate: null})
        }
    }

    //更新状态选择框事件
    handleStatesSelectChange = (value: number | number[] | null) => {
        if (Array.isArray(value)) {
            this.setState({ selectedStates: value.length ? Number(value[0]) : null });
            return;
        }

        const normalizedValue = value === null || value === undefined ? null : Number(value);
        this.setState({ selectedStates: normalizedValue });
    }

    //更新状态日期选择框事件
    handleDateChange = (date: moment.Moment | null) => {
        this.setState({ selectedDate: date });
    };

    //判断是否最后一条记录
    isLatestHistoryRecord = (historyRecordId: number | null) => {
        if (historyRecordId === null) {
            return false;
        }
        const { sourceData } = this.state;
        if (!sourceData.length) {
            return false;
        }
        const lastRecord = sourceData[sourceData.length - 1];
        return lastRecord?.id === historyRecordId;
    };

    //获取删除记录前的上一条记录
    getPreviousHistoryRecord = (historyRecordId: number) => {
        const { sourceData } = this.state;
        if (!sourceData.length) {
            return null;
        }
        const recordIndex = sourceData.findIndex(item => item.id === historyRecordId);
        if (recordIndex > 0) {
            return sourceData[recordIndex - 1];
        }
        return null;
    };

    //同步需求状态到最新记录
    syncRequirementStates = async (states: number) => {
        if (this.props.requirementId === null || this.props.requirementId === undefined) {
            return;
        }
        try {
            const response = await RequirementService.getRequirementById(this.props.requirementId);
            const requirementInfo: IRequirementInfo | undefined = response?.data?.requirementInfo;
            if (!requirementInfo) {
                return;
            }
            const request: RequirementPutParam = {
                id: requirementInfo.id,
                name: requirementInfo.name,
                customer: requirementInfo.customer,
                introduction: requirementInfo.introduction,
                deliveryTime: requirementInfo.deliveryTime,
                budget: requirementInfo.budget,
                states,
            };
            await RequirementService.updateRequirement(request);
        } catch (error) {
            console.error('同步需求状态失败:', error);
        }
    }



    //定义表格的列属性
    columns: ColumnProps<any>[] = [
        { title: '销售', width: 15, dataIndex: 'salesName', key: 'salesName' },
        {
            title: '需求状态',
            width: 20,
            dataIndex: 'states',
            key: 'states',
            render: (state: number) => getRequirementStates(state),
        },
        { title: '需求创建时间', width: 35, dataIndex: 'createTime', key: 'createTime'},      
        {
            title: '操作',
            key: 'operation',
            fixed: 'right',
            width: 10,
            render: (record: RequirementHistoryRecord) => (
                <>
                    <button onClick={() => this.handleEditBottonClick(record)}>编辑</button>  
                    <button onClick={() => this.handleDeleteBottonClick(record)}>删除</button>  
                </>
            ),
        },
    ];

    render() {
        const { sourceData } = this.state;
        const dataWithKeys = sourceData.map((row) => ({ ...row, key: row.id }));
        const selectedRecord = this.state.sourceData.find(item => item.id === this.state.selectRequirementId);

        return (
            <div style={{ width: '100%', overflow: 'auto' }}>
                <Table size="small"
                    columns={this.columns}
                    dataSource={dataWithKeys}
                    scroll={{ x: 580, y: 800 }}
                    pagination={false} 
                />

                <Modal
                    title="编辑需求历史记录"
                    className="updateStatesModal"
                    visible={this.state.isUpdateHistoryModalVisible}
                    onOk={this.handleUpdateHistoryOk}
                    onCancel={() => { this.setState({ isUpdateHistoryModalVisible: false, selectedStates: null, selectedDate: null });}}
                >
                    <div>
                        <label style={{ marginRight: '8px' }}>需求状态:</label>
                        <RequeirementStatesSelect
                            showAllOption={false}
                            initialValues={selectedRecord ? selectedRecord.states : null}
                            mode={undefined}
                            onChange={this.handleStatesSelectChange}
                        />
                    </div>

                    <div style={{ marginTop: '16px' }}>
                        <label style={{ marginRight: '41px' }}>日期:</label>
                        <DatePicker
                            value={this.state.selectedDate ? moment(this.state.selectedDate, 'YYYY-MM-DD') : null}
                            onChange={this.handleDateChange}
                            style={{ width: '50%' }}
                        />
                    </div>
                </Modal>

            </div>
        );
    }

};

export default RequirementHistoryTable;
